import { CoordinatesDto, ISODateTimeString } from "@/types";

export const LATITUDE_MIN = -90;
export const LATITUDE_MAX = 90;
export const LONGITUDE_MIN = -180;
export const LONGITUDE_MAX = 180;

/** Above this radius (metres) a browser fix is almost certainly network-based, not GPS. */
export const COARSE_ACCURACY_THRESHOLD_METRES = 500;

/** A decimal-degree value, optionally signed, using either `.` or `,` as the decimal separator. */
const DECIMAL_DEGREES = /^[+-]?\d{1,3}([.,]\d+)?$/;

/**
 * A "lat, lon" pair as copied from Google Maps and friends.
 *
 * Deliberately restricted to dot-decimals: we also accept `,` as a decimal separator, which makes
 * a string like `59,911, 10,757` genuinely ambiguous. Rather than guess, only split pairs whose
 * decimal separator is unmistakably a dot.
 */
const DOT_DECIMAL_PAIR = /^\s*([+-]?\d{1,3}\.\d+)\s*[,;\s]\s*([+-]?\d{1,3}\.\d+)\s*$/;

/**
 * Parse a hand-typed coordinate into a number.
 *
 * Accepts a comma as the decimal separator, since that is what a Norwegian keyboard layout and
 * locale produce. Returns null for blank or unparseable input — the caller decides whether a
 * blank value is an error.
 */
export function parseCoordinate(input: string | number | null | undefined): number | null {
  if (typeof input === "number") return Number.isFinite(input) ? input : null;
  if (input === null || input === undefined) return null;

  const trimmed = input.trim();
  if (!trimmed || !DECIMAL_DEGREES.test(trimmed)) return null;

  const parsed = Number(trimmed.replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

/** True when the string is blank — coordinates are optional, so blank is a valid state. */
export function isBlankCoordinate(input: string | null | undefined): boolean {
  return !input || input.trim() === "";
}

export function isLatitudeInRange(value: number): boolean {
  return value >= LATITUDE_MIN && value <= LATITUDE_MAX;
}

export function isLongitudeInRange(value: number): boolean {
  return value >= LONGITUDE_MIN && value <= LONGITUDE_MAX;
}

/**
 * Split a pasted "lat, lon" pair so the user can paste straight from a map into either field.
 * Returns null when the input is not an unambiguous pair, leaving it to be handled as a single value.
 */
export function parseCoordinatePair(input: string): { latitude: string; longitude: string } | null {
  const match = DOT_DECIMAL_PAIR.exec(input);
  if (!match) return null;

  const latitude = parseCoordinate(match[1]);
  const longitude = parseCoordinate(match[2]);
  if (latitude === null || longitude === null) return null;
  if (!isLatitudeInRange(latitude) || !isLongitudeInRange(longitude)) return null;

  return { latitude: match[1], longitude: match[2] };
}

/** Render a stored coordinate back into the free-text field. */
export function formatCoordinate(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "";
  return String(value);
}

/**
 * The single mapping chokepoint between form state (free-text strings) and the wire DTO.
 * Returns null when no coordinates were entered, so the order is posted without them.
 */
export function buildPickupCoordinates(
  latitude: string | null | undefined,
  longitude: string | null | undefined,
  capturedAt?: string | null
): CoordinatesDto | null {
  const parsedLatitude = parseCoordinate(latitude);
  const parsedLongitude = parseCoordinate(longitude);
  if (parsedLatitude === null || parsedLongitude === null) return null;

  return {
    latitude: parsedLatitude,
    longitude: parsedLongitude,
    capturedAt: (capturedAt ?? new Date().toISOString()) as ISODateTimeString,
  };
}

/** Human-readable accuracy radius, e.g. "±8 m" or "±4.2 km". */
export function formatAccuracy(metres: number): string {
  if (!Number.isFinite(metres) || metres < 0) return "";
  if (metres < 1000) return `±${Math.round(metres)} m`;
  return `±${(metres / 1000).toFixed(1)} km`;
}

export function isCoarseAccuracy(metres: number | null | undefined): boolean {
  return typeof metres === "number" && metres > COARSE_ACCURACY_THRESHOLD_METRES;
}
